import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Form, Button, InputGroup, Badge, Image, Spinner } from 'react-bootstrap';

// --------- Types ----------
export type ChatMessage = {
  id: string;
  text?: string;
  fileUrl?: string;
  fileName?: string;
  sender: 'me' | 'them';
  timestamp: number; // Date.now()
  status?: 'sending' | 'sent' | 'failed';
  avatarUrl?: string; // optional for "them"
};

export type ChatModuleProps = {
  title?: string;
  meLabel?: string;
  themLabel?: string;
  messages: ChatMessage[];
  typing?: boolean; // show "them is typing…"
  onSend: (payload: { text?: string; file?: File | null }) => Promise<void> | void;
  onRetry?: (message: ChatMessage) => Promise<void> | void;
  allowAttachments?: boolean;
  height?: number | string; // e.g. 520 or "70vh"
};

// --------- Helpers ----------
const formatTime = (t: number) =>
  new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// Simple grouping key: same sender within 5 minutes
const groupKey = (a: ChatMessage, b: ChatMessage) =>
  a.sender === b.sender && Math.abs(a.timestamp - b.timestamp) < 5 * 60 * 1000;

// --------- Component ----------
const ChatModule: React.FC<ChatModuleProps> = ({
  title = 'Chat',
  meLabel = 'You',
  themLabel = 'Contact',
  messages,
  typing = false,
  onSend,
  onRetry,
  allowAttachments = true,
  height = 560,
}) => {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-scroll to bottom on new messages/typing
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, typing]);

  // Group messages for visual cohesion
  const grouped = useMemo(() => {
    const out: Array<{ head: ChatMessage; items: ChatMessage[] }> = [];
    for (const m of messages) {
      const last = out[out.length - 1];
      if (last && groupKey(last.head, m)) {
        last.items.push(m);
      } else {
        out.push({ head: m, items: [m] });
      }
    }
    return out;
  }, [messages]);

  const handleSend = async () => {
    const cleanText = text.trim();
    if (!cleanText && !file) return;

    try {
      setSending(true);
      await onSend({ text: cleanText || undefined, file });
      setText('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setSending(false);
    }
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Card className="shadow-sm border-0">
      {/* Header */}
      <Card.Header className="bg-white border-0 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <strong>{title}</strong>
          {typing && (
            <Badge bg="light" text="dark" className="border">
              typing…
            </Badge>
          )}
        </div>
        <div className="small text-muted">{themLabel}</div>
      </Card.Header>

      {/* Body */}
      <Card.Body className="pt-0">
        <div className="border rounded p-3 bg-light" style={{ height, overflowY: 'auto' }}>
          {grouped.length === 0 && !typing && (
            <div className="text-center text-muted mt-5">No messages yet</div>
          )}

          {grouped.map(({ head, items }) => {
            const isMe = head.sender === 'me';
            return (
              <div key={head.id + head.timestamp} className="mb-3">
                {/* Sender Row */}
                <div className={`d-flex ${isMe ? 'justify-content-end' : 'justify-content-start'}`}>
                  {!isMe && head.avatarUrl && (
                    <Image
                      src={head.avatarUrl}
                      roundedCircle
                      width={28}
                      height={28}
                      className="me-2 align-self-end"
                    />
                  )}

                  <div
                    className={`d-flex flex-column ${isMe ? 'align-items-end' : 'align-items-start'}`}
                  >
                    {/* Name + time (first bubble) */}
                    <div className="small text-muted mb-1">
                      {isMe ? meLabel : themLabel} • {formatTime(head.timestamp)}
                    </div>

                    {/* Bubbles */}
                    {items.map((m) => {
                      const bubbleClasses = isMe ? 'bg-primary text-white' : 'bg-white border';
                      return (
                        <div
                          key={m.id}
                          className={`rounded-3 px-3 py-2 ${bubbleClasses} mb-1`}
                          style={{ maxWidth: 520 }}
                        >
                          {m.text && <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>}

                          {m.fileUrl && (
                            <div className="mt-1">
                              <a
                                href={m.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={isMe ? 'link-light' : 'link-primary'}
                              >
                                {m.fileName || 'Attachment'}
                              </a>
                            </div>
                          )}

                          {/* Status for my messages */}
                          {isMe && (
                            <div className="d-flex align-items-center gap-2 mt-1">
                              {m.status === 'sending' && (
                                <span className="small d-inline-flex align-items-center gap-1">
                                  <Spinner animation="border" size="sm" /> sending
                                </span>
                              )}
                              {m.status === 'sent' && <span className="small">✓ sent</span>}
                              {m.status === 'failed' && (
                                <Button
                                  size="sm"
                                  variant="outline-light"
                                  className="py-0 px-2"
                                  onClick={() => onRetry?.(m)}
                                >
                                  retry
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Typing bubble */}
          {typing && (
            <div className="d-flex justify-content-start">
              <div className="bg-white border rounded-3 px-3 py-2">
                <span className="opacity-50">•••</span>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Composer */}
        <div className="mt-3">
          <InputGroup>
            {allowAttachments && (
              <>
                <Button variant="outline-secondary" onClick={() => fileInputRef.current?.click()}>
                  📎
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="d-none"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </>
            )}

            <Form.Control
              as="textarea"
              rows={1}
              placeholder="Type a message"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
            />

            <Button
              variant="success"
              disabled={sending || (!text.trim() && !file)}
              onClick={handleSend}
            >
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </InputGroup>

          {file && (
            <div className="mt-2 small d-flex align-items-center justify-content-between border rounded px-2 py-1 bg-white">
              <span className="text-truncate">{file.name}</span>
              <Button size="sm" variant="link" onClick={() => setFile(null)}>
                remove
              </Button>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ChatModule;
